// NumberInput — numeric field per COMPONENTS §Catalog + §Input + §Per-primitive
// WCAG. Replaces raw <input type="number"> (whose scroll-wheel mutation,
// lost-keystrokes-on-invalid, ignored maxLength, and locale-comma handling are
// the quirks this primitive exists to avoid).
//
// Renders <input type="text" inputmode="decimal"> so mobile still gets a
// numeric keypad while we own parsing. Value model: `value: number | null`
// (null = empty/cleared) + `onChange(value: number | null)`. A local string
// draft lets the user type freely ("12.", "0,") without the parent number
// clobbering mid-edit; the parsed number is emitted as they type, and the draft
// is re-normalised (fixed decimals, percent clamp) on blur.
//
// - `decimals` (0…4) caps fractional digits (a currency's minor units go to 3, the books column to 4); 0 forbids a decimal separator.
// - `percent` clamps to 0–100 and rounds to an integer on blur.
// - Accepts both '.' and ',' as the decimal separator (fr input); normalises to
//   '.' internally. Non-negative by default.
// Validation is form-state-level (RHF + Zod resolver per ADR-008 — wire via
// Controller); the `error` prop just renders the visual message.

import {
  useEffect,
  useId,
  useRef,
  useState,
  forwardRef,
  type InputHTMLAttributes,
} from 'react';
import { useSelectOnFocus } from '../../hooks/useSelectOnFocus.js';

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> {
  value: number | null;
  onChange: (value: number | null) => void;
  /** Max fractional digits. Default 0 (integers only). */
  decimals?: 0 | 1 | 2 | 3 | 4;
  /** Clamp to 0–100 and round to an integer on blur. */
  percent?: boolean;
  /** Error message displayed below; sets aria-invalid + aria-describedby. */
  error?: string;
  /** Select the contents on focus so typing replaces the number (default true). */
  selectOnFocus?: boolean;
}

/** number → display string at the given precision (null/NaN → ''). */
function format(value: number | null, decimals: number): string {
  if (value === null || Number.isNaN(value)) return '';
  return decimals > 0 ? value.toFixed(decimals) : String(Math.trunc(value));
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { value, onChange, decimals = 0, percent = false, error, id: idProp, className = '', onBlur, disabled, selectOnFocus = true, onFocus, onMouseUp, ...props },
  ref,
) {
  const reactId = useId();
  const id = idProp ?? reactId;
  const errorId = `${id}-error`;
  const [draft, setDraft] = useState(() => format(value, decimals));
  // Track the last value we emitted so an external value change (form reset,
  // programmatic set) re-syncs the draft, but our own keystroke echoes don't.
  const lastEmitted = useRef(value);
  // The commit-on-blur handler routes THROUGH the hook (never past it) so
  // select-on-focus can tell a fresh entry from a re-focus — see useSelectOnFocus.
  const focusHandlers = useSelectOnFocus(selectOnFocus, { onFocus, onBlur: handleBlur, onMouseUp });

  useEffect(() => {
    if (value !== lastEmitted.current) {
      setDraft(format(value, decimals));
      lastEmitted.current = value;
    }
  }, [value, decimals]);

  function parse(raw: string): number | null {
    const normalised = raw.replace(',', '.');
    if (normalised === '' || normalised === '.') return null;
    const n = Number(normalised);
    return Number.isNaN(n) ? null : n;
  }

  function handleChange(raw: string) {
    // Keep only digits + a single decimal separator (',' or '.'). decimals===0
    // forbids the separator outright.
    let cleaned = raw.replace(decimals === 0 ? /[^\d]/g : /[^\d.,]/g, '');
    if (decimals > 0) {
      const firstSep = cleaned.search(/[.,]/);
      if (firstSep !== -1) {
        const head = cleaned.slice(0, firstSep + 1);
        const tail = cleaned.slice(firstSep + 1).replace(/[.,]/g, '');
        cleaned = head + tail.slice(0, decimals);
      }
    }
    setDraft(cleaned);
    const parsed = parse(cleaned);
    lastEmitted.current = parsed;
    onChange(parsed);
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    let parsed = parse(draft);
    if (parsed !== null && percent) parsed = Math.min(100, Math.max(0, Math.round(parsed)));
    setDraft(format(parsed, percent ? 0 : decimals));
    if (parsed !== lastEmitted.current) {
      lastEmitted.current = parsed;
      onChange(parsed);
    }
    onBlur?.(e);
  }

  return (
    <div className="space-y-1">
      <input
        ref={ref}
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        value={draft}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => handleChange(e.target.value)}
        {...focusHandlers}
        className={
          // Border + hover + focus light-up + invalid: the shared `field-frame`
          // utility (DESIGN-TOKENS §Interaction states); aria-invalid drives the
          // error border. Never re-inline the recipe.
          // pointer-coarse:text-base — iOS input-zoom guard (canonical note: Input.tsx).
          'w-full px-3 py-2 text-sm pointer-coarse:text-base rounded-md outline-none transition-colors ' +
          'bg-[var(--color-bg-page)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] ' +
          'disabled:bg-[var(--color-bg-surface-alt)] disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed ' +
          'read-only:bg-[var(--color-bg-surface-alt)] ' +
          `field-frame ${className}`
        }
        {...props}
      />
      {error && (
        <p id={errorId} className="text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
});
